import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { ModalComponent } from 'app/shared/components/modal/modal.component';
import { ValueStreamConfiguration } from '@app/shared/models/value-stream-configuration.model';
import { Line } from '@app/shared/models/line.model';
import { LineService } from '@app/core/services/line.service';
import { ValueStreamService } from '@app/core/services/value-stream.service';

import { Log } from 'ng2-logger';
import { PermissionService } from '@app/authentication/services/permission.service';
import { PickList } from 'primeng/picklist';

@Component({
    selector: 'app-value-stream-create-modal',
    templateUrl: './value-stream-create-modal.component.html',
    styleUrls: ['./value-stream-create-modal.component.scss']
})
export class ValueStreamCreateModalComponent implements OnInit {

    @ViewChild(ModalComponent, { static: true }) modal: ModalComponent;
    @ViewChild(PickList, { static: true }) pickList: PickList;

    @Output() onCreate = new EventEmitter<any>();
    @Output() onUpdate = new EventEmitter<any>();
    @Output() onDelete = new EventEmitter<any>();

    @Input() isLoading;
    @Input() lines: Line[] = [];

    valueStream = new ValueStreamConfiguration();

    form: UntypedFormGroup;
    selectedLines: Line[] = [];
    availableValueStreams: any[] = [];

    previousName = '';
    duplicateMode = false;

    showValidations = false;
    validationErrors: any;

    _logger = Log.create(ValueStreamCreateModalComponent.name);

    constructor(
        private lineService: LineService,
        private vsService: ValueStreamService,
        private permissionService: PermissionService
    ) {}

    ngOnInit() {
        this.form = new UntypedFormGroup({
            valueStreamName: new UntypedFormControl('', [Validators.required]),
            emailAddress: new UntypedFormControl('', [Validators.required, Validators.email])
        });

        if (this.shouldRenderModifiers()) {
            this.loadLines();
            this.loadAvailableValueStreams();
        }
    }

    open(vs = new ValueStreamConfiguration(), duplicate = false) {
        this.reset();
        this.loadFormValue(vs);

        if (this.shouldRenderModifiers()) {
            this.loadLines();
            this.loadAvailableValueStreams();
        }

        this.duplicateMode = duplicate;
        this.modal.open();
    }

    close() {
        this.modal.close();
    }

    loadFormValue(vs: ValueStreamConfiguration) {
        this.valueStream = vs;
        this.previousName = vs.valueStreamName;
        const currentVSObj = {
            label: vs.valueStreamName,
            value: vs.valueStreamName
        };
        this.availableValueStreams.push(currentVSObj);
        this.form.setValue({
            valueStreamName: currentVSObj,
            emailAddress: vs.emailAddress || ''
        });

        this.selectedLines = vs.lines || [];
    }

    onLinesSelectionChange(event) {
        this.valueStream.lines = this.selectedLines;
    }

    createVS() {
        if (!this.isFormValid()) return;

        this.onCreate.emit(this.getFormValue());
    }

    updateVS() {
        if (!this.isFormValid()) return;

        this.onUpdate.emit({
            previousName: this.previousName,
            newValueStream: this.getFormValue()
        });
    }

    deleteVS() {
        this.onDelete.emit(this.getFormValue());
    }

    getFormValue() {
        const raw = this.form.getRawValue();
        return {
            ...this.valueStream,
            valueStreamName: raw.valueStreamName?.value ?? raw.valueStreamName,
            emailAddress: raw.emailAddress,
            lines: this.valueStream.lines
        };
    }

    get isEditMode() {
        return !!this.valueStream.id && !this.duplicateMode;
    }

    private loadLines() {
        this.lineService.getAllLines().subscribe(
            (data: any) => this.lines = data.items,
            error => this._logger.error(error)
        );
    }

    private loadAvailableValueStreams() {
        this.vsService.getAvailableValueStreams().subscribe(
            (values: string[]) => {
                this.availableValueStreams = values.map(v => ({
                    label: v,
                    value: v
                }));
            },
            error => this._logger.error(error)
        );
    }

    private reset() {
        this.form.reset();
        this.showValidations = false;
        this.validationErrors = {};
        this.duplicateMode = false;
        this.valueStream = new ValueStreamConfiguration();
    }

    private isFormValid() {
        this.validationErrors = {};
        this.showValidations = true;
        let isValid = true;

        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.controls[key];
            if (control.invalid) {
                isValid = false;
            }
        });

        return isValid;
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_VALUE_STREAMS');
    }
}
